import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { DateTime } from "@/Common/Helpers/DateTime";
import { PopulationBasedFundingHelper } from "@/Common/Helpers/PopulationBasedFundingHelper";
import { SchedulingTabViews } from "@/Common/Models/UI/Enums";
import { PredefinedRuleSet } from "../Api/Dto/Responses/PredefinedRuleSet";
import { CalendarAppointmentDisplay } from "../Models/Calendar/CalendarAppointmentDisplay";
import { IJCreateTemplateTabModel } from "../Models/JSONInterfaces/IJCreateTemplateTabModel";
import { IJOpenScheduleTabModel } from "../Models/JSONInterfaces/IJOpenScheduleTabModel";
import { IJSchedulingTabModel } from "../Models/JSONInterfaces/IJSchedulingTabModel";
import { IJTemplateTabModel } from "../Models/JSONInterfaces/IJTemplateTabModel";
import { ServiceDefinitionWithItsRelatedCombos } from "../Models/Scheduling/ComboServiceModel";
import { MinMaxCalculationResult } from "../Models/Scheduling/MinMaxCalculationResult";

class SchedulerHelper {
    public static GetCsvValueByIndex(csv: string, idx: number): string {
        var result = "";
        try {
            result = csv.replace(/(&#39;)+/g, "\'").split(";")[idx]; // remove double quotes because kendo split them on new lines
        }
        catch (err) { }
        return result;
    }

    public static GetValueOrEmpty(str: string, includeBrackets: boolean = false, includeParenthesis: boolean = false): string {
        var result = "";

        if (str) {
            result = str;
            if (includeBrackets)
                result = "[" + result + "]";
            if (includeParenthesis)
                result = " (" + result + ")";
        }
        return result;
    }

    public static GetValueOrZero(val: number, includeBrackets: boolean = false): string {
        var result = "";
        if (val) {
            result = val.toString();
            if (includeBrackets)
                result = "[" + result + "]";
        }
        else
            result = "0";

        return result;
    }

    public static GetValueConditional(val: number, conditional: boolean): string {
        var result = "";
        if (conditional) {
            if (val) {
                result = val.toString();
            }
            else
                result = "0";
        }
        return result;
    }

    public static getDefaultSchedulingTabModel(): IJSchedulingTabModel {
        var patientModel: IJSchedulingTabModel;

        patientModel = {
            CurrentView: SchedulingTabViews.OpenSchedule
        };
        return patientModel;
    }

    public static getDefaultOpenScheduleTabModel(): IJOpenScheduleTabModel {
        var model: IJOpenScheduleTabModel =
        {
            ComboServiceListFiltered: [],
            SelectedRuleset: null
        };

        return model;
    }

    public static getDefaultTemplateTabModel(): IJTemplateTabModel {
        var model: IJTemplateTabModel = {
            TemplateList: [],
            SelectedTemplateName: "",
            SelectedTemplateId: "",
            SelectedTemplateWorkDay: [],
            SearchText: "",
            IntervenorsList: [],
            OpenIntervenorPopup: false,
            IsRuleSetTemplate: false,
            DeletePopupOpen: false
        };

        return model;
    }

    public static getDefaultCreateTemplateTabModel(): IJCreateTemplateTabModel {
        const typeList = [
            new ComboBoxDropDownItem("0", cacheStore.GetString("ServiceLabel"), 0, null),
            new ComboBoxDropDownItem("1", cacheStore.GetString("RuleSetLabel"), 1, null)
        ];

        var model: IJCreateTemplateTabModel = {
            WorkDayList: [],
            Name: "",
            HourStart: new Date(DateTime.nowDeprecated().getFullYear(), DateTime.nowDeprecated().getMonth(), DateTime.nowDeprecated().getDate(), 8),
            HourEnd: new Date(DateTime.nowDeprecated().getFullYear(), DateTime.nowDeprecated().getMonth(), DateTime.nowDeprecated().getDate(), 12),
            SelectedSiteId: cacheStore.SiteCache.length > 1 ? sessionStore.getCurrentEmployee().getActiveSite().Model.Id : cacheStore.SiteCache[0].Model.Id,
            IsFromEdit: false,
            EditionTemplateId: "",
            CreateScheduleTypeCode: "0",
            CreateScheduleTypeCodeList: typeList,
            OpenAddServicePopup: false,
            SelectedComboServiceList: []
        };

        return model;
    }

    public static calculateTotalMinAndMax(ruleset: ServiceDefinitionWithItsRelatedCombos[], start: Date, end: Date): MinMaxCalculationResult {
        // calculate group minimums to ensure the total minimum durations is not superior to the duration of the rulest
        var totalMinDuration: number = 0;
        ruleset.forEach(n => {
            var durationInMinutes = (n.ServiceDefinitionDuration > 600000000) ? n.ServiceDefinitionDuration / 600000000 : n.ServiceDefinitionDuration;
            var SumMinDurations = 0;

            n.RelatedServiceDefinitionContexts.forEach(s => {
                if (!s.IsCombo) {
                    if (s.Min > 0) {
                        SumMinDurations += durationInMinutes * s.Min;
                    }
                }
            });
            totalMinDuration = totalMinDuration + SumMinDurations;
        });

        var diff = (end.getTime() - start.getTime()) / 60000;

        // calculate group maxes to ensure the total max durations is not inferior to the duration of the rulest
        var calculatedGroupMaxes: number[] = [];
        ruleset.forEach(rs => {
            let durationInMinutes = (rs.ServiceDefinitionDuration > 600000000) ? rs.ServiceDefinitionDuration / 600000000 : rs.ServiceDefinitionDuration;

            // if serviceComboGroup has a groupMax, we take the groupmax
            if (rs.GroupMax != null && rs.GroupMax > 0) {
                calculatedGroupMaxes.push(rs.GroupMax * durationInMinutes);
            }
            // if serviceComboGroup has no groupMax, and all subitems have a maxValue, we grab total(subitems.Max)
            else if ((rs.GroupMax == null || rs.GroupMax == 0) && rs.RelatedServiceDefinitionContexts.every(sd => sd.Max != null && sd.Max > 0)) {
                let subitemax = 0;
                rs.RelatedServiceDefinitionContexts.forEach(sd => { subitemax += sd.Max; });
                calculatedGroupMaxes.push(subitemax * durationInMinutes);
            }
            // else the group has no max or has a subitem with no max, meaning we can put unlimited nb of that service/combo, that makes the MAX rule valid
            // we push a null to detect it later
            else {
                calculatedGroupMaxes.push(null);
            }
        });

        var minmaxResults = new MinMaxCalculationResult();
        minmaxResults.MinimumsValid = totalMinDuration <= diff;

        return minmaxResults;
    }

    public static isServiceListComplex(services: Immutable.List<ServiceDefinitionWithItsRelatedCombos>): boolean {
        if (!services?.count())
            return false;

        if (services.count() !== 1)
            return true;

        const contexts = services.first().RelatedServiceDefinitionContexts;

        if (!contexts?.length)
            return false;

        if (contexts.length !== 1)
            return true;

        const context = contexts[0];

        return context.IsCombo
            || !!context.Min
            || (context.Max > 0 && context.Max < 300); // an item without a max is set to 300 to simplify calculations
    }

    public static isPredefinedRuleSetComplex(ruleSet: PredefinedRuleSet): boolean {
        if (!ruleSet?.PredefinedRuleSetItems?.length)
            return false;

        if (ruleSet.PredefinedRuleSetItems.length !== 1)
            return true;

        const ruleSetItem = ruleSet.PredefinedRuleSetItems[0];

        return !!ruleSetItem.ComboId
            || !!ruleSetItem.Min
            || (ruleSetItem.Max > 0 && ruleSetItem.Max < 300); // an item without a max is set to 300 to simplify calculations
    }

    public static getPopulationBasedFundingStatusIcon(data: CalendarAppointmentDisplay): string {
        return PopulationBasedFundingHelper.determineStatusIcon(
            data.PatientPopulationBasedFundingStatus?.LatestPBFRegistrationAction,
            data.PatientPopulationBasedFundingStatus?.WillContinueTreatment,
            data.PatientPopulationBasedFundingStatus?.IsPendingOrOverriddenProposal,
            data.PatientPopulationBasedFundingStatus?.BatchEligibilityCheckStatusEnum,
            data.PatientPopulationBasedFundingStatus?.IsRegisteredWithStarredFamilyPhysician
        ).toString();
    }

    public static getPopulationBasedFundingPratictionerName(data: CalendarAppointmentDisplay): string {
        return Employee.getFullNameWithPrefixContactFacet(data.PatientPopulationBasedFundingStatus?.RegisteredPhysicianContactFacetId) ?? "";
    }

    public static HtmlEncode(str: string): string {
        return str.replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
}

export { SchedulerHelper };
